import React, { useMemo, useState, useEffect } from 'react';
import { useStore } from '@/state/store';
import { pageDisplayName } from '@/lib/pageNames';
import { useI18n } from '@/i18n/I18nContext';
import { deviceLabel } from '@/lib/deviceIcons';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { DeviceIcon } from '@/components/DeviceIcon';
import { ChevronLeft, ChevronRight, FileText, LayoutGrid } from 'lucide-react';

// Quick device finder (name / IP) across all pages. A device lives on exactly
// one standard page (its owner) but may be a member of several detail drawings.
// Selecting a device that appears on multiple pages opens a page picker so the
// user can choose which drawing to open; single-page devices jump directly.
export const SearchDialog = ({ open, onOpenChange }) => {
  const { t, language } = useI18n();
  const doc = useStore((s) => s.doc);
  const focusNode = useStore((s) => s.focusNode);
  const focusNodeOnPage = useStore((s) => s.focusNodeOnPage);
  const [picker, setPicker] = useState(null);
  const [query, setQuery] = useState('');

  // Reset transient state whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setPicker(null);
      setQuery('');
    }
  }, [open]);

  const devices = useMemo(() => {
    const list = [];
    const standardPages = (doc.pages || []).filter((p) => p.type !== 'detail');
    const detailPages = (doc.pages || []).filter((p) => p.type === 'detail');
    standardPages.forEach((p) => {
      (p.nodes || []).forEach((n) => {
        if (n.data?.aliasOf) return; // skip clone placements — master is listed once
        const pages = [{ id: p.id, name: pageDisplayName(p.name, language), type: 'standard' }];
        // Other standard pages where this device is placed as a clone.
        standardPages.forEach((sp) => {
          if (sp.id === p.id) return;
          if ((sp.nodes || []).some((x) => x.data?.aliasOf === n.id)) {
            pages.push({ id: sp.id, name: pageDisplayName(sp.name, language), type: 'standard' });
          }
        });
        detailPages.forEach((dp) => {
          if ((dp.members || []).some((m) => m.deviceId === n.id)) {
            pages.push({ id: dp.id, name: pageDisplayName(dp.name, language), type: 'detail' });
          }
        });
        list.push({
          id: n.id,
          name: n.data?.label || deviceLabel(n.data?.deviceType, language),
          deviceType: n.data?.deviceType,
          ip: n.data?.ip || '',
          page: pageDisplayName(p.name, language),
          pages,
        });
      });
    });
    return list;
  }, [doc, language]);

  const onPickDevice = (d) => {
    if (d.pages.length <= 1) {
      focusNode(d.id);
      onOpenChange(false);
    } else {
      setQuery('');
      setPicker(d);
    }
  };

  const onPickPage = (deviceId, pageId) => {
    focusNodeOnPage(deviceId, pageId);
    onOpenChange(false);
  };

  const goBack = () => {
    setQuery('');
    setPicker(null);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder={picker ? picker.name : t('search_placeholder')}
        value={query}
        onValueChange={setQuery}
        data-testid="search-input"
      />
      <CommandList>
        <CommandEmpty>{t('search_no_results')}</CommandEmpty>

        {!picker ? (
          <CommandGroup heading={t('devices')}>
            {devices.map((d) => (
              <CommandItem
                key={d.id}
                value={`${d.name} ${d.ip} ${d.page}`}
                onSelect={() => onPickDevice(d)}
                data-testid={`search-result-${d.id}`}
                className="gap-2.5"
              >
                <DeviceIcon type={d.deviceType} size={22} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-[#111418]">{d.name}</div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {d.ip ? <span className="font-mono">{d.ip}</span> : <span className="italic">{t('no_vlan')}</span>}
                    <span> · {d.page}</span>
                  </div>
                </div>
                {d.pages.length > 1 ? (
                  <span
                    className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-[#0B5FFF]/10 px-2 py-0.5 text-[10px] font-medium text-[#0B5FFF]"
                    data-testid={`search-pagecount-${d.id}`}
                  >
                    {d.pages.length} {t('pages_word')}
                    <ChevronRight className="h-3 w-3" />
                  </span>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : (
          <CommandGroup heading={`${t('search_open_on')} · ${picker.name}`}>
            <CommandItem
              value="__back zurück back"
              onSelect={goBack}
              data-testid="search-back"
              className="gap-2 text-muted-foreground"
            >
              <ChevronLeft className="h-4 w-4" /> {t('search_back')}
            </CommandItem>
            {picker.pages.map((pg) => (
              <CommandItem
                key={pg.id}
                value={`${picker.name} ${pg.name} ${pg.type}`}
                onSelect={() => onPickPage(picker.id, pg.id)}
                data-testid={`search-page-${pg.id}`}
                className="gap-2.5"
              >
                {pg.type === 'detail' ? (
                  <LayoutGrid className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <FileText className="h-4 w-4 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-[#111418]">{pg.name}</div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {pg.type === 'detail' ? t('detail_page') : t('standard_page')}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};
